import { faPaperPlane } from "@fortawesome/pro-solid-svg-icons"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { Button, Input, Modal, notification, Space, Switch, Typography } from "antd"
import { useEffect, useRef, useState } from "react"
import VSpace from "../common/VSpace"
import { Consult } from "./customerApi"
import { useSendConsultationInvitesMutation } from "./customerQueries"
import { buildInviteAddresses, buildSendInvitesPayload, InviteAddress } from "./customersModel"

const { Text } = Typography

type Props = {
  open: boolean
  customerId: number
  consult: Consult | null
  customerEmails: { address: string; isPrimary?: boolean }[]
  onClose: () => void
}

const emptyInvite = { addresses: [] as InviteAddress[], replyToAddresses: [] as InviteAddress[] }

const SendConsultationInvitesModal = ({ open, customerId, consult, customerEmails, onClose }: Props) => {
  const [invite, setInvite] = useState(emptyInvite)
  const sendMutation = useSendConsultationInvitesMutation()
  const initializedRef = useRef(false)

  useEffect(() => {
    if (!open) {
      initializedRef.current = false
      return
    }
    if (consult && !initializedRef.current) {
      initializedRef.current = true
      setInvite(buildInviteAddresses({
        organizerEmail: consult.organizerEmail ?? '',
        customerEmails,
        createdBy: consult.createdBy,
      }))
    }
  }, [open, consult, customerEmails])

  const toggleAddress = (index: number, selected: boolean) => {
    setInvite(prev => ({ ...prev, addresses: prev.addresses.map((a, i) => (i === index ? { ...a, selected } : a)) }))
  }

  const updateReplyTo = (index: number, patch: Partial<InviteAddress>) => {
    setInvite(prev => ({ ...prev, replyToAddresses: prev.replyToAddresses.map((a, i) => (i === index ? { ...a, ...patch } : a)) }))
  }

  const send = () => {
    if (!consult) {
      return
    }
    sendMutation.mutate(
      { customerId, consultId: consult.id, emailAddresses: buildSendInvitesPayload(invite) },
      {
        onSuccess: () => {
          notification.success({ message: 'Your invites have been sent to you and this customer.' })
          onClose()
        },
      }
    )
  }

  return <Modal
    open={open}
    title={<span><FontAwesomeIcon icon={faPaperPlane} /> Send Consultation Invites</span>}
    width={720}
    onCancel={onClose}
    footer={[
      <Button key="cancel" onClick={onClose}>Cancel</Button>,
      <Button key="send" type="primary" icon={<FontAwesomeIcon icon={faPaperPlane} />} loading={sendMutation.isLoading} onClick={send}>
        Send Invites
      </Button>,
    ]}
  >
    <VSpace size="middle">
      <div>
        <Text>Select the sales person and client addresses to send the consultation invitation email:</Text>
        <VSpace size={6} style={{ marginTop: 8 }}>
          {invite.addresses.map((address, index) => <Space key={index} size={8}>
            <Switch size="small" checked={address.selected} onChange={selected => toggleAddress(index, selected)} />
            <Text>{address.address}</Text>
          </Space>)}
        </VSpace>
      </div>

      <div>
        <Text>Select the reply to email addresses:</Text>
        <VSpace size={6} style={{ marginTop: 8 }}>
          {invite.replyToAddresses.map((address, index) => <Space key={index} size={8}>
            <Switch size="small" checked={address.selected} onChange={selected => updateReplyTo(index, { selected })} />
            <Input type="email" value={address.address} onChange={e => updateReplyTo(index, { address: e.target.value })} style={{ width: 320 }} />
          </Space>)}
        </VSpace>
      </div>
    </VSpace>
  </Modal>
}

export default SendConsultationInvitesModal
